import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Col, Container, FormGroup, Input, Label, Row, Table } from 'reactstrap';
import { Breadcrumbs, ScreenOverlay, Spinner } from '../../components/Common';
import useAuthUser from '../../common/hooks/authUser';
import {
	dealerInventorySearchPath,
	isDealerPortalNavFromInventory,
} from '../../common/dealerPortalNavigation';
import type { TDealerPortalNavState } from '../../common/dealerPortalNavigation';
import { toastError } from '../../helpers/toast-helper';
import { getFormattedCurrency } from '../../helpers/currency-helper';
import { getTableFormattedDate } from '../../helpers/date-time-helper';
import { normalizeCustomerNumberForLookup } from '../../helpers/dealer-customer-input';
import { getDealerPortalInvoices } from '../../api/dealerPortal';
import type { TSyteLineInvoice } from '../../api/dealerPortal/types';

function formatInvoiceDate(value: unknown): string {
	if (value == null || value === '') return '—';
	const s = typeof value === 'string' ? value : String(value);
	return getTableFormattedDate(s) || '—';
}

const DealerInvoicesPage = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const [searchParams] = useSearchParams();
	const { user, isAdmin, isDataLoading } = useAuthUser();
	const [invoices, setInvoices] = useState<TSyteLineInvoice[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [invNumberInput, setInvNumberInput] = useState('');
	const [orderNumberInput, setOrderNumberInput] = useState('');
	const [customerPoInput, setCustomerPoInput] = useState('');
	const redirectDoneRef = useRef(false);

	const customerNumber = useMemo(() => {
		const raw = searchParams.get('customerNumber') ?? '';
		return normalizeCustomerNumberForLookup(raw);
	}, [searchParams]);

	const fetchInvoices = useCallback(
		async (filters?: { invNumber?: string; orderNumber?: string; customerPO?: string }) => {
			if (!customerNumber) return;
			setIsLoading(true);
			setInvoices([]);
			try {
				const list = await getDealerPortalInvoices({
					customerNumber,
					invNumber: filters?.invNumber,
					orderNumber: filters?.orderNumber,
					customerPO: filters?.customerPO,
				});
				const arr = Array.isArray(list) ? list : [];
				const filtered = arr.filter((row) => row.invoiceNumber);
				if (arr.length > 0 && filtered.length === 0 && arr[0]?.errorMessage) {
					toastError(arr[0].errorMessage);
				}
				setInvoices(filtered);
			} catch (e) {
				toastError(e instanceof Error ? e.message : 'Failed to load invoices.');
			} finally {
				setIsLoading(false);
			}
		},
		[customerNumber],
	);

	useEffect(() => {
		document.title = 'Invoices';
	}, []);

	useEffect(() => {
		if (user && !isAdmin) {
			toastError('Access denied. Admin privileges required.');
			navigate('/');
		}
	}, [user, isAdmin, navigate]);

	useEffect(() => {
		if (!user || !isAdmin) return;
		const allowed = isDealerPortalNavFromInventory(location.state);
		if (!allowed || !customerNumber) {
			if (!redirectDoneRef.current) {
				redirectDoneRef.current = true;
				toastError('Open invoices from Dealer inventory after selecting a dealer.');
				navigate('/admin/dealer-inventory', { replace: true });
			}
			return;
		}
		void fetchInvoices();
	}, [user, isAdmin, location.state, customerNumber, navigate, fetchInvoices]);

	const handleSearch = useCallback(() => {
		const inv = invNumberInput.trim();
		const ord = orderNumberInput.trim();
		const po = customerPoInput.trim();
		if (!inv && !ord && !po) {
			toastError('Enter at least one of Invoice #, Order #, or Customer PO to search.');
			return;
		}
		void fetchInvoices({
			invNumber: inv || undefined,
			orderNumber: ord || undefined,
			customerPO: po || undefined,
		});
	}, [invNumberInput, orderNumberInput, customerPoInput, fetchInvoices]);

	const handleClearSearch = useCallback(() => {
		setInvNumberInput('');
		setOrderNumberInput('');
		setCustomerPoInput('');
		void fetchInvoices();
	}, [fetchInvoices]);

	if (isDataLoading) {
		return <Spinner />;
	}

	if (user && !isAdmin) {
		return null;
	}

	if (!isDealerPortalNavFromInventory(location.state) || !customerNumber) {
		return null;
	}

	const navState = { fromDealerInventory: true } satisfies TDealerPortalNavState;

	return (
		<Container fluid>
			<Breadcrumbs title="Invoices" />
			<Row className="mb-3">
				<Col>
					<Button
						tag={Link}
						to={dealerInventorySearchPath(customerNumber)}
						color="light"
						className="me-2 mb-2"
						type="button"
					>
						← Dealer inventory
					</Button>
				</Col>
			</Row>
			<Row className="mb-3">
				<Col md={8}>
					<p className="mb-0 text-muted small">Customer number</p>
					<p className="mb-0 fw-semibold">{customerNumber}</p>
				</Col>
			</Row>

			<Row className="g-3 mb-3 align-items-end">
				<Col md={4} lg={3}>
					<FormGroup className="mb-0">
						<Label for="invSearchInv">Invoice #</Label>
						<Input
							id="invSearchInv"
							autoComplete="off"
							value={invNumberInput}
							onChange={(e) => setInvNumberInput(e.target.value)}
							onKeyDown={(e) => {
								if (e.key === 'Enter') handleSearch();
							}}
						/>
					</FormGroup>
				</Col>
				<Col md={4} lg={3}>
					<FormGroup className="mb-0">
						<Label for="invSearchOrder">Order #</Label>
						<Input
							id="invSearchOrder"
							autoComplete="off"
							value={orderNumberInput}
							onChange={(e) => setOrderNumberInput(e.target.value)}
							onKeyDown={(e) => {
								if (e.key === 'Enter') handleSearch();
							}}
						/>
					</FormGroup>
				</Col>
				<Col md={4} lg={3}>
					<FormGroup className="mb-0">
						<Label for="invSearchPo">Customer PO</Label>
						<Input
							id="invSearchPo"
							autoComplete="off"
							value={customerPoInput}
							onChange={(e) => setCustomerPoInput(e.target.value)}
							onKeyDown={(e) => {
								if (e.key === 'Enter') handleSearch();
							}}
						/>
					</FormGroup>
				</Col>
				<Col md="auto" className="d-flex flex-wrap gap-2">
					<Button color="primary" type="button" onClick={() => void handleSearch()} disabled={isLoading}>
						Search
					</Button>
					<Button color="light" type="button" onClick={() => void handleClearSearch()} disabled={isLoading}>
						Clear search
					</Button>
				</Col>
			</Row>

			<div
				className="position-relative"
				style={isLoading ? { minHeight: '12rem' } : undefined}
			>
				{isLoading && (
					<>
						<ScreenOverlay />
						<Spinner />
					</>
				)}
				<div className="table-responsive">
					<Table bordered hover size="sm" className="align-middle">
						<thead className="table-light">
							<tr>
								<th>Date</th>
								<th>Invoice #</th>
								<th>Order #</th>
								<th>Customer PO</th>
								<th>Due date</th>
								<th className="text-end">Total amount</th>
								<th style={{ width: 80 }} />
							</tr>
						</thead>
						<tbody>
							{!isLoading && invoices.length === 0 && (
								<tr>
									<td colSpan={7} className="text-muted">
										No invoices returned.
									</td>
								</tr>
							)}
							{invoices.map((inv, idx) => {
								const num = inv.invoiceNumber ?? '';
								const detailTo = `/admin/invoices/${encodeURIComponent(num)}?customerNumber=${encodeURIComponent(customerNumber)}`;
								return (
									<tr key={`${num}-${idx}`}>
										<td>{formatInvoiceDate(inv.invoiceDate)}</td>
										<td>{num || '—'}</td>
										<td>{inv.orderNumber ?? '—'}</td>
										<td>{inv.customerPO ?? '—'}</td>
										<td>{formatInvoiceDate(inv.dueDate)}</td>
										<td className="text-end">{getFormattedCurrency(inv.invoiceAmount)}</td>
										<td>
											{num ? (
												<Link to={detailTo} state={navState}>
													view
												</Link>
											) : (
												<span className="text-muted">—</span>
											)}
										</td>
									</tr>
								);
							})}
						</tbody>
					</Table>
				</div>
			</div>
		</Container>
	);
};

export default DealerInvoicesPage;
